"use client";

/**
 * Everything about the account rather than about the work.
 *
 * The three things in here were in three different places: the handles were a
 * card in the middle of Home, the sign-out was a door glyph at the foot of the
 * rail and again as a text link in the footer, and the theme could not be
 * changed from the portal at all. None of them belong to a page — they belong
 * to the person — which is why they are one dialog reached from the identity
 * row rather than three pieces of furniture on Home.
 *
 * IT IS A LIST OF ROWS. The first version was three headed groups, and the
 * headings were the problem rather than the fix: at 15/600 against 14 body
 * they were a third level of text within six points of the other two, so a
 * sheet meant to be scanned read as a page of prose. Two of them also labelled
 * a single row each — "Account" over an email, "Your handles" over two
 * fields — which is a heading doing the work a label already did.
 *
 * So there are no headings. Every setting is one row, and the rows are
 * interchangeable: what it is on the left, why it exists under that, the
 * control on the right against a shared edge. Hierarchy is the title, then the
 * rows, then sign-out set apart at the foot — three levels that are actually
 * different rather than three sizes of the same one.
 *
 * THE EMAIL IS AN OBJECT, not a line of prose. It has been three things now:
 * a row under an "Account" heading labelled "Signed in as", then the dialog's
 * description under the title. The second was better and still wrong, because
 * a description explains what a dialog is for and an address explains nothing
 * — it is a value. Dropped into a prose slot it belonged to nothing, with
 * near-equal space above and below, so it read as text left in a gap.
 *
 * It is now a pill beside the title, which attaches it to the one thing on
 * the sheet that was never going to move. It was briefly a plate on the
 * recessed ground with an avatar — the same pair the rail's foot row shows —
 * and that solved belonging by giving the address its own object; the pill
 * solves it by making the address part of the heading instead.
 *
 * ONE COST, STATED. `badge-styles.ts` defines a badge as content — "one value
 * from a small set" — and an address is an unbounded identifier, so this is a
 * pill holding the kind of thing the rule was written to keep out of pills. It
 * also has to truncate: past the title and a 44px close target a phone leaves
 * roughly two hundred points, which a long agency address will exceed. The
 * full value stays reachable through `title`, which is a hover affordance and
 * so is worth nothing on the device where the clipping happens.
 *
 * Radix wants a description and there is honestly nothing to say that the
 * title does not, so the sheet opts out of `aria-describedby` rather than
 * inventing a sentence for a screen reader to read out on every open.
 *
 * THE FOOTER IS WHERE THE SHEET ENDS, and both of the things that end it live
 * there. Save was inside the handles group, sitting in the control column
 * between Instagram and Appearance, which put a committing action in the
 * middle of the list and left a hole in the column beside it. Sign-out was
 * below the list with section spacing above it, separated by a gap that had to
 * be large enough to mean something.
 *
 * A footer answers both at once: it is the conventional place a dialog keeps
 * its actions, so neither needs a gap to argue it is not a row, and the bar
 * they sit on separates them from the list by being a different kind of thing
 * rather than by distance. Sign-out takes the left, quiet; Save takes the
 * right, where the eye finishes.
 *
 * Save is only ever about the handles — appearance applies on the press, and
 * you can see that it did. It is disabled rather than absent while there is
 * nothing to commit, so the footer keeps its shape and the button keeps its
 * place; a control that moves with state is one the hand has to find again.
 *
 * TWO SHAPES, ONE DECISION. Past rail width it is a centred dialog; on a
 * phone it is a bottom sheet, which is the presentation the design system's
 * `Sheet` already argues for — a bottom edge is where the thumb is and where
 * the panel came from, and it means nothing at a desk. The branch is in
 * JavaScript rather than in `lg:` classes because both trees would otherwise
 * stay mounted: `display: none` does not reach portalled content, and two
 * open overlays are two focus traps fighting over the keyboard.
 *
 * The rows and the actions are written once and handed to whichever shape is
 * rendering. What differs between them is only where those go — the dialog has
 * a header and a footer bar, the sheet has a dragged header and a pinned
 * footer, and the email that is the dialog's description becomes the first
 * line of the sheet's body, because a sheet header takes a title and nothing
 * else.
 *
 * NOT YET CONVERGED with `prototypes/one/_components/Sheet.tsx`, which makes
 * the same choice for the prototype's four overlays and says so in its own
 * docstring. That one wraps the prototypes' `Modal`; this one wraps the design
 * system's, whose header/footer composition the settings sheet uses. Folding
 * them into one chooser is worth doing and is its own change — doing it here
 * would mean rebuilding this sheet against the weaker of the two modals.
 *
 * IT WEARS `ds-next` ITSELF. Radix portals the content to `document.body`, and
 * `/one` wears the candidate layer on a `display: contents` div inside the
 * route — so a portalled dialog lands outside it and renders in Framework's
 * palette. The class goes on the content for the same reason the route has it.
 */

import { Loader2, LogOut } from "lucide-react";
import type { ReactNode, RefObject } from "react";
import { Button } from "@/components/ui/design-system/button";
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "@/components/ui/design-system/modal";
import { Sheet } from "@/components/ui/design-system/sheet";
import { useIsWide } from "@/components/ui/design-system/use-is-wide";
import { useCreatorTranslations } from "@/i18n/creator/useCreatorTranslations";
import { cn } from "@/lib/utils";
import { AppearanceSetting } from "./AppearanceSetting";
import { LanguageSetting } from "./LanguageSetting";
import { PORTAL_OVERLAY, ROW_PRESS, TOUCH } from "./one/chrome";
import { Pill } from "./one/ui";

export function SettingsDialog({
  open,
  onOpenChange,
  email,
  handles,
  onSignOut,
  wide,
  frame,
  depthRootId,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  email: string;
  /**
   * The handles form, supplied by the host rather than built here — the same
   * slot arrangement the rail uses for its program switcher, and for the same
   * reason. The product hands in `useCreatorHandles`, which knows about access
   * and tRPC; the prototype has no handles to edit and hands in nothing, and
   * both the rows and the Save button then do not appear. One sheet serves
   * both without the product growing a fixture or the prototype growing a data
   * layer.
   *
   * Rows and commit state rather than one node, because they land in different
   * parts of the sheet: the fields in the list, the button in the footer.
   */
  handles?: {
    rows: ReactNode;
    clean: boolean;
    saving: boolean;
    submit: () => void;
  };
  onSignOut: () => void;
  /**
   * Force the presentation instead of measuring the window.
   *
   * The prototype runs inside a simulated device, so the question "is there
   * room for a dialog" is about that frame and not the browser — and it
   * already measures it for every other overlay. A second observer on the same
   * element can disagree with the first for a frame.
   */
  wide?: boolean;
  /** Sheet only: the device frame, so it stays inside the phone it belongs to. */
  frame?: RefObject<HTMLElement | null>;
  /** Sheet only: the element that recedes behind it. Unique per device. */
  depthRootId?: string;
}) {
  const t = useCreatorTranslations("settings");
  const measured = useIsWide();
  const asDialog = wide ?? measured;

  /**
   * Who you are, as the plate the rail's foot row hands over.
   *
   * 28px rather than the rail's 24: this is the first object in a sheet
   * instead of one row in a column of five, and an initial that reads as a
   * bullet is not an avatar. Not pressable and no chevron — there is nothing
   * behind it, and a plate that looks like a row is a row that does nothing.
   */
  const identity = (
    <Pill
      title={email}
      className="min-w-0 max-w-[200px] truncate sm:max-w-[340px]"
    >
      {email}
    </Pill>
  );

  const rows = (
    <>
      {handles?.rows}

      <AppearanceSetting />

      <LanguageSetting />
    </>
  );

  const signOut = (
    <button
      type="button"
      onClick={onSignOut}
      className={cn(
        // Pulled back by its own padding so the glyph starts on the same left
        // edge as the rows above. The padding still has to be there — it is
        // the shape the press fills — but a control indented by it has left
        // the sheet's margin.
        "-ml-nf-ds-3 flex items-center gap-nf-ds-item rounded-[14px] px-nf-ds-3 text-left outline-hidden",
        // Muted until you reach for it. A permanently red control is the
        // brightest thing in a sheet you opened to change a handle, and it
        // shouts about the one action nobody came here for; the colour belongs
        // to the moment the pointer is on it.
        "text-muted-foreground hover:text-nf-ds-destructive",
        "focus-visible:text-nf-ds-destructive focus-visible:ring-2 focus-visible:ring-nf-ds-brand",
        TOUCH,
        ROW_PRESS,
      )}
    >
      <LogOut className="size-[18px] shrink-0" strokeWidth={2} />
      <span className="font-nf-ds-sans text-[14px] font-[500]">
        {t("signOut")}
      </span>
    </button>
  );

  const save = handles ? (
    <Button
      variant="brand"
      disabled={handles.clean || handles.saving}
      onClick={handles.submit}
    >
      {handles.saving ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          {t("saving")}
        </>
      ) : (
        t("save")
      )}
    </Button>
  ) : null;

  if (!asDialog) {
    return (
      <Sheet
        open={open}
        onClose={() => onOpenChange(false)}
        title={t("title")}
        titleTrailing={identity}
        className={PORTAL_OVERLAY}
        mountPoint={frame?.current ?? undefined}
        contained={!!frame}
        depthRootId={depthRootId}
        footer={
          <div className="flex items-center justify-between gap-nf-ds-item">
            {signOut}
            {save}
          </div>
        }
      >
        <div className="flex flex-col gap-nf-ds-block pt-nf-ds-2">{rows}</div>
      </Sheet>
    );
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent
        size="lg"
        // Nothing to say that the title does not — see the note above.
        aria-describedby={undefined}
        className={PORTAL_OVERLAY}
      >
        <ModalHeader className="flex-row items-center gap-nf-ds-2">
          <ModalTitle className="shrink-0">{t("title")}</ModalTitle>
          {identity}
        </ModalHeader>

        <div className="flex flex-col gap-nf-ds-block">{rows}</div>

        <ModalFooter className="sm:justify-between">
          {signOut}
          {save}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
